import { HighchartsChart, IconFa } from '@/components/wm';
import { cn } from '@/utils';
import React, { useEffect, useMemo, useState } from 'react';

import { axisDecimals, chartColors, chartPeriodInterval, DEFAULT_RATE_BY_PERIOD } from '../utils';

// How this widget renders each of the shared PALETTE_OPTIONS. Private on purpose:
// the palette names are the shared vocabulary, the classes are not.
const paletteVariants = {
    primary: 'tw-bg-primary tw-text-secondary',
    secondary: 'tw-bg-secondary tw-text-gray-200',
    simple: 'tw-bg-white tw-border tw-border-solid tw-border-border tw-text-secondary'
};

const iconVariants = {
    primary: 'tw-text-secondary',
    secondary: 'tw-text-gray-200',
    simple: 'tw-text-secondary'
};

// `sample`, when given, stands in for the indicator and its data: the type picker
// shows each widget with made-up values.
export default function IndicatorChartWidget({ sample, widget }) {
    const
        [points, setPoints] = useState(sample?.points ?? null),
        [failed, setFailed] = useState(false);

    const
        // Widgets saved before the chart type existed were all bar charts.
        chartType = widget.graphical.chartType || 'column',
        model = WiseMetering.indicators.get(widget.data.indicatorId),
        palette = widget.graphical.palette || 'primary',
        period = widget.data.period,
        rate = widget.data.rate || DEFAULT_RATE_BY_PERIOD[period] || 3600;

    const
        decimals = widget.data.decimals,
        icon = WiseMetering.icons.getIconFromKind(model?.get('kind_slug')) || 'info',
        timezone = model?.timezone() || moment.tz.guess(),
        unit = sample?.unit ?? (model?.get('unit') || ''),
        unitScale = widget.data.unitScale >= 0 ? widget.data.unitScale : undefined;

    // Fetches raw values only. Formatting lives in the options below, so that
    // changing decimals or the unit scale redraws without re-querying, and so
    // this effect does not re-run every time GridStack rewrites the layout.
    useEffect(() => {
        if (sample || !model) {
            return;
        }

        let cancelled = false;

        // Back to loading, so switching indicator in the wizard preview does not
        // leave the previous indicator's series on screen until the fetch lands.
        setPoints(null);

        const fetchData = () => {
            const interval = chartPeriodInterval(period, model.timezone());

            model.getRealtimeData({ from: interval.from, to: interval.to, rate }).then(response => {
                if (cancelled) {
                    return;
                }

                const data = Array.isArray(response?.data) ? response.data : [];

                // `t` is a unix timestamp in seconds; Highcharts wants milliseconds.
                setPoints(data.filter(point => point.v !== null).map(point => [point.t * 1000, point.v]));
                setFailed(false);
            }).catch(() => {
                if (!cancelled) {
                    setPoints([]);
                    setFailed(true);
                }
            });
        };

        fetchData();

        const refresh = setInterval(fetchData, 60000);

        return () => {
            cancelled = true;
            clearInterval(refresh);
        };
    }, [model, period, rate]);

    // Memoised: HighchartsChart applies a new options object to the chart, so an
    // object rebuilt on every render would redraw the chart on every render.
    const options = useMemo(() => {
        const colors = chartColors(palette);

        return {
            chart: {
                backgroundColor: 'transparent',
                // The widget already pads its content, so this is just enough margin
                // to stop the last x-axis label being clipped at the right edge.
                spacing: [8, 8, 0, 0]
            },
            credits: { enabled: false },
            exporting: { enabled: false },
            legend: { enabled: false },
            plotOptions: {
                column: {
                    borderWidth: 0,
                    groupPadding: 0.05
                },
                line: {
                    lineWidth: 1,
                    marker: { enabled: false },
                    // Each value is a bucket total, so it holds flat, as on the Plot tab.
                    step: 'left'
                }
            },
            // On the series, not `chart.type`: `chart.update` does not reliably
            // retype a series that inherits the chart's type.
            series: [{
                color: colors.series,
                data: points || [],
                name: widget.title,
                type: chartType
            }],
            time: { timezone },
            title: { text: null },
            tooltip: {
                formatter: function() {
                    const stamp = moment(this.x).tz(timezone).format('YYYY/MM/DD HH:mm');

                    return `${stamp}<br/><b>${formatValue(this.y, unit, decimals, unitScale)}</b>`;
                }
            },
            xAxis: {
                labels: { style: { color: colors.text } },
                lineColor: colors.text,
                tickColor: colors.text,
                type: 'datetime'
            },
            yAxis: {
                gridLineColor: colors.grid,
                gridLineWidth: 1,
                labels: {
                    formatter: function() {
                        return formatValue(this.value, unit, axisDecimals(this.axis.tickPositions, unit, decimals, unitScale), unitScale);
                    },
                    style: { color: colors.text }
                },
                title: { text: null }
            }
        };
    }, [chartType, decimals, palette, points, timezone, unit, unitScale, widget.title]);

    const message = !model && !sample
        ? i18next.t('ui.no_data_available')
        : points === null
            ? i18next.t('modal.title.loading')
            : (points.length === 0 || failed) && i18next.t('ui.no_data_available_for_the_current_period');

    return (
        <div
            className={cn(
                paletteVariants[palette],
                'tw-flex tw-flex-col tw-gap-1 tw-rounded-sm tw-px-4 tw-pt-4',
                'tw-h-full tw-w-full tw-overflow-hidden'
            )}
            id={widget.id}
        >
            {/* Header (top) */}
            <div>
                <div className="tw-flex tw-items-center tw-justify-between">
                    <div className="tw-text-base tw-font-medium tw-leading-none">{widget.title}</div>
                    <IconFa color={iconVariants[palette]} icon={icon} size="medium"/>
                </div>
                <div className="tw-text-md tw-mt-0">
                    {widget.subtitle || ''}
                </div>
            </div>
            {/* Out of the flow: an in-flow chart keeps the height it was drawn at
                and props the card open, so the card clips it. */}
            <div className="tw-relative tw-flex-1 tw-min-h-0">
                <div className="tw-absolute tw-inset-0 tw-pb-2">
                    {message
                        ? <div className="tw-h-full tw-flex tw-items-center tw-justify-center tw-text-sm">{message}</div>
                        : <HighchartsChart options={options}/>
                    }
                </div>
            </div>
        </div>
    );
}
